import { z } from 'zod/v4';

import {
    animationPluginShapeLegacy,
    buttonHorizontalPositionSchema,
    extendedPositionPluginShapeLegacy,
    selectedFileSchemaLegacy,
    shadowPluginShapeLegacy,
    visibilityPluginShapeLegacy,
    widgetPositionPluginShapeLegacy,
} from '../../../common/schemas/legacy';

/**
 * Resize strategy, mirroring `EResizeType` from the host app's image type
 * module. `'scale'` constrains the image by `styles.maxWidth`; `'fill'`
 * stretches it into a fixed (resizable) box via aspect-ratio padding.
 */
export const imageResizeTypeSchema = z.enum(['scale', 'fill']);

/**
 * Background-position keyword for the zero-block variant (`EPositionOptions` —
 * `'center'`, `'left top'`, …). Kept as `z.string()` so the package doesn't
 * mirror the host enum; the view binds it straight to `object-position`.
 */
export const imagePositionSchema = z.string();

/**
 * Data shape for the **default-placement** image variant — the canonical image
 * data schema; the zero variant derives from it via `.extend()`.
 *
 * - `image` holds the picked file (`SelectedFileExtended`) or `null` (empty
 *   placeholder) — see {@link selectedFileSchemaLegacy}.
 * - `horizontalAlign` reuses the shared `'L'|'M'|'R'` bucket; the per-device
 *   twins are `nullish()` (host types mark them optional, consumers fall back
 *   to `horizontalAlign`).
 * - `isSynchronizeStyle` is `nullable()`: very old widgets persisted `null`,
 *   which the editor treats as `true` (truthy check).
 * - `position` is the simple per-device grid (`nullish()` — pre-position-group
 *   widgets omit it); shadow is the standard toggle + settings pair.
 *
 * Mirrors the FormGroup produced by `getDefaultImageWidgetData` in the host app.
 */
export const imageLegacyDefaultDataSchema = z.object({
    image: selectedFileSchemaLegacy.nullable(),
    horizontalAlign: buttonHorizontalPositionSchema,
    mobileHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    tabletHorizontalAlign: buttonHorizontalPositionSchema.nullish(),
    isBorder: z.boolean(),
    isIndependentCorners: z.boolean(),
    resizeType: imageResizeTypeSchema,
    isSynchronizeStyle: z.boolean().nullable(),
    ...visibilityPluginShapeLegacy,
    ...animationPluginShapeLegacy,
    ...shadowPluginShapeLegacy,
    ...widgetPositionPluginShapeLegacy,
});

/**
 * Data shape for the **zero-block-placement** image variant — the default
 * schema with `position` overridden (zod v4 `.extend()` overwrites existing
 * keys) to the full extended-position grid, plus the zero-only `imagePosition`
 * keyword (the user drags the image's focal point inside its fixed box, which
 * the default variant doesn't expose).
 *
 * Mirrors the FormGroup produced by `getZeroImageWidgetData` in the host app.
 */
export const imageLegacyZeroDataSchema = imageLegacyDefaultDataSchema.extend({
    ...extendedPositionPluginShapeLegacy,
    imagePosition: imagePositionSchema,
});
